import { Card } from "shared/components/Card";
import FailedIcon from "shared/icons/status-icons/fail-icon.svg?react";
import SuccessIcon from "shared/icons/status-icons/success-icon.svg?react";
import { RuleSet, RuleSetDecision, Rule as RuleT } from "../types";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  Button,
  ButtonSize,
  ButtonVariant,
  Hide,
  Show
} from "shared/components";
import { cx } from "shared/utils/classnames";
import { Plus } from "untitledui-js-base";
import { Dispatch, Fragment, SetStateAction } from "react";
import { Rule } from "./Rule";
import { defaultRule } from "../constants";
import { VerticalSpacer } from "./VerticalSpacer";
import { v4 } from "uuid";
import { useTemplateContext } from "shared/app/VerificationTemplate/provider";

export const RuleSetCard = ({
  decision,
  ruleNumber,
  ruleSets,
  setRuleSets
}: {
  decision: RuleSetDecision;
  ruleNumber: number;
  ruleSets: RuleSet[];
  setRuleSets?: Dispatch<SetStateAction<RuleSet[]>>;
}) => {
  const { readOnly } = useTemplateContext();

  const { Icon, iconColor, title } =
    decision === RuleSetDecision.Approve
      ? {
          Icon: SuccessIcon,
          iconColor: "text-success-500",
          title: "Recommend approval when"
        }
      : {
          Icon: FailedIcon,
          iconColor: "text-danger-500",
          title: "Recommend decline when"
        };

  const updateRuleSet = (id: string, newRules: RuleT[]) => {
    setRuleSets?.((prev) => {
      return prev.map((rs) => {
        if (rs.id === id)
          return {
            ...rs,
            rules: [...newRules]
          };

        return rs;
      });
    });
  };

  return (
    <Card>
      <div className="px-6 py-5 flex items-center gap-2 border-b border-alpha-40 flex-wrap md:flex-nowrap">
        <Icon className={cx("w-5 shrink-0", iconColor)} />
        <h2 className="text-base font-bold text-content-1000">
          {ruleNumber}. {title}
        </h2>

        <Hide if={readOnly}>
          <Button
            className="ml-auto"
            Icon={Plus}
            size={ButtonSize.SM}
            variant={ButtonVariant.PRIMARY}
            onClick={() => {
              setRuleSets?.((prev) => [
                ...prev,
                {
                  id: v4(),
                  type: decision,
                  rules: []
                }
              ]);
            }}
          >
            Add rule
          </Button>
        </Hide>
      </div>
      <div className="p-6">
        {ruleSets.map((ruleSet, idx) => (
          <Fragment key={ruleSet.id}>
            <Show if={idx !== 0}>
              <div className="flex gap-6 items-center my-4">
                <Badge variant={BadgeVariant.BASE} size={BadgeSize.BASE}>
                  OR
                </Badge>
                <div className="w-full h-px bg-alpha-40"></div>
              </div>
            </Show>
            <Card className="!bg-background-900 px-6 py-8 relative border-dashed animate-appear">
              <Badge
                variant={BadgeVariant.OUTLINE}
                size={BadgeSize.BASE}
                className="absolute -top-3 left-1/2 -translate-x-1/2"
              >
                Rule {idx + 1}
              </Badge>
              {ruleSet.rules.map((rule, index) => (
                <Fragment key={decision + "_" + index + "_" + rule}>
                  <Show if={index !== 0}>
                    <div className="-translate-y-1">
                      <VerticalSpacer height={16} className="ml-6" />
                      <Badge
                        className="ml-1"
                        variant={BadgeVariant.BASE}
                        size={BadgeSize.SM}
                      >
                        And
                      </Badge>
                      <VerticalSpacer height={16} className="ml-6" />
                    </div>
                  </Show>
                  <Rule
                    updateRule={(field: string, value: string | undefined) => {
                      ruleSet.rules[index][field] = value;
                      updateRuleSet(ruleSet.id, ruleSet.rules);
                    }}
                    removeRule={() => {
                      const newRules = [...ruleSet.rules];
                      newRules.splice(index, 1);
                      updateRuleSet(ruleSet.id, newRules);
                    }}
                    {...rule}
                  />
                </Fragment>
              ))}
              <Show if={ruleSet.rules.length !== 0 && !readOnly}>
                <VerticalSpacer height={24} className="ml-6 -translate-y-1" />
              </Show>

              <Hide if={readOnly}>
                <Button
                  variant={ButtonVariant.BASE}
                  size={ButtonSize.SM}
                  Icon={Plus}
                  onClick={() => {
                    const newRules = ruleSet.rules;

                    newRules.push({
                      id: v4(),
                      ...defaultRule
                    });

                    updateRuleSet(ruleSet.id, newRules);
                  }}
                >
                  Add condition
                </Button>
              </Hide>
              <Show if={ruleSets.length > 1 && !readOnly}>
                <div className="flex flex-col">
                  <div className="h-px w-full bg-alpha-40 my-4" />
                  <Button
                    className="ml-auto !text-content-300 hover:!text-danger-500"
                    size={ButtonSize.XS}
                    variant={ButtonVariant.TEXT}
                    onClick={() => {
                      setRuleSets?.((prev) =>
                        prev.filter((rs) => rs.id !== ruleSet.id)
                      );
                    }}
                  >
                    Remove condition set
                  </Button>
                </div>
              </Show>
            </Card>
          </Fragment>
        ))}
      </div>
    </Card>
  );
};
